import { Field, FieldProps, useFormikContext } from 'formik'
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
} from '@chakra-ui/react'
import { useMapsLibrary } from '@vis.gl/react-google-maps'
import { useEffect, useRef, useState } from 'react'

export type FormValues = {
  address: string
}

interface AddressAutocompleteV3Props {
  name: string
  label?: string
  isDisabled?: boolean // name 'isDisabled' is important for compatibility with the Chakra <Input
  placeholder?: string
}

// This is the "react way" to handle google maps autocomplete

const AddressAutocompleteV3 = (props: AddressAutocompleteV3Props) => {
  const {
    name = 'address',
    label = 'Address',
    isDisabled = false,
    placeholder = 'Enter your address',
  } = props
  const { setFieldValue } = useFormikContext<FormValues>()
  const inputRef = useRef<HTMLInputElement>(null)
  const [
    placeAutocomplete,
    setPlaceAutocomplete,
  ] = useState<google.maps.places.Autocomplete | null>(null)
  const placesLib = useMapsLibrary('places')

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (!placesLib || !inputRef.current) {
      return
    }

    const options = {
      types: ['address'],
      componentRestrictions: { country: 'us' },
      fields: ['formatted_address', 'geometry', 'name'],
    }

    setPlaceAutocomplete(new placesLib.Autocomplete(inputRef.current, options))
  }, [placesLib])

  // Add place_changed listener
  useEffect(() => {
    if (!placeAutocomplete) {
      return
    }

    const listener = placeAutocomplete.addListener('place_changed', () => {
      const place = placeAutocomplete.getPlace()

      if (place?.formatted_address) {
        // Update Formik field value directly
        setFieldValue(name, place.formatted_address)
      }
    })

    return () => {
      if (placeAutocomplete && listener) {
        google.maps.event.removeListener(listener)
      }
    }
  }, [placeAutocomplete, setFieldValue])

  return (
    <Field name={name}>
      {({ field, form }: FieldProps<string, FormValues>) => (
        <FormControl
          isInvalid={Boolean(
            form.errors[name as keyof FormValues] &&
              form.touched[name as keyof FormValues]
          )}
          mb={8}
        >
          <FormLabel htmlFor={name}>{label}</FormLabel>
          <Input
            {...field}
            ref={inputRef}
            id={name}
            placeholder={placeholder}
            autoComplete="off"
            isDisabled={isDisabled}
          />
          <FormErrorMessage>
            {form.errors[name as keyof FormValues]}
          </FormErrorMessage>
        </FormControl>
      )}
    </Field>
  )
}

export default AddressAutocompleteV3
